import React, { useCallback, useEffect, useId, useState } from "react";

import type { SolverSettingsModalProps } from "@/components/modals/SolverSettingsModal.types.ts";
import type { DepartmentConfigs, SolverBaseOptions, SolverBlockLogicOptions, SolverConfigs, SolverConstructionHeuristicOptions, SolverMandatoryEntityOptions } from "@/types.ts";
import type { SolverSettingOptionsController, SolverSettingsOptionsProps } from "@/components/sections/SolverSettingsOptions.types.tsx";
import type { SolverPhasesController, SolverPhasesProps } from "@/components/sections/SolverPhases.types.tsx";
import { CalendarRange, ChevronRight, PlusCircle, Settings} from "lucide-react";
import Button from "@/components/button/Button.tsx";
import CollapsibleSection from "@/components/sections/CollapsibleSection.tsx";

// function safeParseInputTimeSecondsToMilli(seconds:string):number{
//     const parseSeconds = parseFloat(seconds||"0");
//     if(isNaN(parseSeconds))return 0;
//     return Math.floor(parseSeconds * 1000);
// }

function safeParseInputIntNumber(seconds:string):number{
    const parseSeconds = parseFloat(seconds||"0");
    if(isNaN(parseSeconds))return 0;
    return Math.floor(parseSeconds);
}

const DEFAULT_LOOK_BACKWARD_IN_DAYS = 28;

// function initOptionNumber(value:number | undefined | null, defaultInitValue:number):number{
//     if(value === void 0  || value ===null)return defaultInitValue;
//     return value
// }



export const SolverPhaseInputGroupWrapper: React.FC<{children: React.ReactNode , onClick? : ()=>void}> = (props)=>{
    return <div
        onClick={props.onClick}
        className={`px-4 py-0 border-1 border-gray-300 rounded-sm my-2` + (props.onClick ? ' cursor-pointer' : '')}>
        {props.children}
    </div>
}
const RadioButton: React.FC<{
    checked: boolean,
    onChange:React.ChangeEventHandler<HTMLInputElement, HTMLInputElement> | undefined
}> = (props)=>{
    return <input
        type="radio"
        checked={props.checked}
        disabled={false}
        onChange={props.onChange}
        className="h-4 w-4 accent-blue-600"
    />
}


const FlexWrapper:React.FC<{children: React.ReactNode}>  = (props) =>{
return <div className="flex items-center gap-2 items-center min-h-12">
        {props.children}
    </div>
}

export const PhaseInner: React.FC<{
    title : string,
    selectable :boolean
    configurable? :boolean
    checked: boolean,
    onChange:React.ChangeEventHandler<HTMLInputElement, HTMLInputElement> | undefined
    //children: React.ReactNode
}> = (props)=>{
    return <FlexWrapper >
        {props.selectable ===true? <RadioButton
            checked={props.checked}
            onChange={props.onChange}
        /> : ''}
        <span className={`text-sm`}>{props.title}</span>
        {props.configurable !== false ? <div className="ml-auto">
            <Button type="primary" icon={<Settings size={20}/>} />
        </div> : ''}
    </FlexWrapper>
}






export function buildSolverPhasesController(
    depConfigs  : DepartmentConfigs | undefined,
    selectedPhaseId?:string,
    preSelectPhase?:boolean
) : SolverPhasesController{
    const [editData , setEditData] = useState({...{},...depConfigs});
    const mapOfPhases = editData.mapOfPhases || {};
    var entries =Object.entries(mapOfPhases);
    var listOfUIPhases:SolverPhasesController["viewData"]["listOfUIPhases"] = [];

    for (const [key, value] of entries) {
        if(value && value.active !== false){
            listOfUIPhases.push({
                name : value.name || "-",
                id : key,
                phase : value
            });
        }
    }
    // const collator = new Intl.Collator("en", {
    // sensitivity: "base"
    // });

    // items.sort((a, b) => collator.compare(a.name, b.name));

    listOfUIPhases.sort((a, b) =>
        a.name.localeCompare(b.name, "en", {
            sensitivity: "base"
        })
    );


    if(preSelectPhase ===true && !selectedPhaseId && listOfUIPhases[0]){
        selectedPhaseId = listOfUIPhases[0].id;
    }

    const viewData:SolverPhasesController["viewData"] = {
        listOfUIPhases : listOfUIPhases,
        selectedPhaseId : selectedPhaseId,
        selectedPhase : mapOfPhases[selectedPhaseId || ""]
    }


    return {
        setEditData,
        editData,
        viewData
    }
}


/**
 * Main component
 * @param param0
 * @returns
 */
const SolverPhases: React.FC<SolverPhasesProps> = (props) => {

    //const [editData , setEditData] = useState({...{},...solverConfigs});
    const ctrl =props.controller;
    const setEditData = ctrl.setEditData;
    const editData = ctrl.editData;
    const viewData = ctrl.viewData;
    const [phasesOpen, setPhasesOpen] = useState(true);
    const lookBackwardId = useId();
    const hasPhases = viewData.listOfUIPhases.length > 0;

    return (
         <div className="text-gray-700">

                {!hasPhases && (
                    <SolverPhaseInputGroupWrapper onClick={props.selectGlobalConfig}>
                        <FlexWrapper>
                            <Settings />
                            <span className={`text-sm`}>{'Configurations de résolution'}</span>
                        </FlexWrapper>
                    </SolverPhaseInputGroupWrapper>
                )}

                <SolverPhaseInputGroupWrapper onClick={props.selectDateFilter}>
                    <FlexWrapper>
                        <CalendarRange />
                        <span className={`text-sm`}>{'Filtre de dates'}</span>
                        {props.dateFilter && (
                            <span className="text-xs text-gray-500 ml-1">
                                {props.dateFilter.startDate} à {props.dateFilter.endDate}
                            </span>
                        )}
                        <div className="ml-auto">
                            <Button type="primary" icon={<Settings size={20}/>} />
                        </div>
                    </FlexWrapper>
                </SolverPhaseInputGroupWrapper>
                <SolverPhaseInputGroupWrapper>
                    <div className="flex items-center gap-3 justify-start my-1">
                        {props.lookBackwardStatus === "ready" ? (
                            <label htmlFor={lookBackwardId} className="text-sm max-w-[200px]">
                                Période de regard pour les quarts passés (en jour)
                            </label>
                        ) : (
                            <span className="text-sm max-w-[200px]">
                                Période de regard pour les quarts passés (en jour)
                            </span>
                        )}
                        {props.lookBackwardStatus === "loading" && (
                            <span
                                className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
                                role="status"
                                aria-label="Chargement du réglage"
                            />
                        )}
                        {props.lookBackwardStatus === "error" && (
                            <span className="text-xs text-red-600">
                                Indisponible — rouvre cette fenêtre pour réessayer.
                            </span>
                        )}
                        {props.lookBackwardStatus === "ready" && (
                            <input
                                id={lookBackwardId}
                                type="number"
                                min="0"
                                placeholder={String(DEFAULT_LOOK_BACKWARD_IN_DAYS)}
                                value={props.lookBackwardInDays ?? ''}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    props.onChangeLookBackwardInDays?.(raw === '' ? null : Math.max(0, safeParseInputIntNumber(raw)));
                                }}
                                className="w-16 py-1 text-center border border-gray-400 rounded focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        )}
                    </div>
                </SolverPhaseInputGroupWrapper>

                {props.onOpenWeekendGrid && (
                    <SolverPhaseInputGroupWrapper onClick={props.onOpenWeekendGrid}>
                        <FlexWrapper>
                            <Settings />
                            <span className="text-sm">Identifications de quarts de fin de semaine</span>
                            <div className="ml-auto"><ChevronRight /></div>
                        </FlexWrapper>
                    </SolverPhaseInputGroupWrapper>
                )}

                <br></br>
                <CollapsibleSection
                    title="Phases"
                    count={viewData.listOfUIPhases.length}
                    open={phasesOpen}
                    onToggle={() => setPhasesOpen((prev) => !prev)}
                    headerClassName="text-base font-bold"
                    contentClassName="pt-0"
                >
                    {
                        viewData.listOfUIPhases.map((item)=>{
                            return  <SolverPhaseInputGroupWrapper key={item.id} onClick={() => props.selectPhase(item.id)}>
                                <PhaseInner
                                    title={item.name}
                                    selectable={false}
                                    checked={item.id === viewData.selectedPhaseId}
                                    // onChange={(e) => props.selectPhase(item.id)}
                                    onChange={(e) => {}}
                                />
                            </SolverPhaseInputGroupWrapper>

                        })
                    }
                </CollapsibleSection>

                <SolverPhaseInputGroupWrapper onClick={props.addPhase}>
                    <FlexWrapper>
                        <PlusCircle />
                        <span className={`text-sm`}>{'Ajouter une phase'}</span>
                    </FlexWrapper>
                </SolverPhaseInputGroupWrapper>


         </div>
    );
};

export default SolverPhases;
